import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const PatientContextBar = ({ patient, alerts = [], onAlertClick }) => {
  const navigate = useNavigate();
  const [isAlertsExpanded, setIsAlertsExpanded] = useState(false);

  const criticalAlerts = alerts?.filter(alert => alert?.severity === 'critical');
  const warningAlerts = alerts?.filter(alert => alert?.severity === 'warning');
  const infoAlerts = alerts?.filter(alert => alert?.severity === 'info');

  const totalAlerts = alerts?.length;

  const handleAlertClick = (alert) => {
    if (onAlertClick) {
      onAlertClick(alert);
    }
  };

  if (!patient) {
    return null;
  }

  return (
    <div className="patient-context-bar">
      <div className="max-w-screen-2xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
              <Icon name="User" size={24} color="var(--color-primary)" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-foreground truncate">
                {patient?.name}
              </h2>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="font-mono">MRN: {patient?.mrn}</span>
                <span>DOB: {patient?.dob}</span>
                <span className="hidden sm:inline">Age: {patient?.age}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {totalAlerts > 0 && (
              <button
                onClick={() => setIsAlertsExpanded(!isAlertsExpanded)}
                className="flex items-center gap-2 px-4 h-10 bg-muted rounded-lg text-sm font-medium text-foreground transition-smooth hover:bg-muted/80"
              >
                <Icon name="Bell" size={18} />
                <span className="hidden sm:inline">Alerts</span>
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-destructive text-destructive-foreground text-xs font-semibold rounded-full">
                  {totalAlerts}
                </span>
              </button>
            )}

            <Button
              variant="outline"
              size="sm"
              iconName="FileText"
              iconPosition="left"
              onClick={() => navigate('/patient-records')}
            >
              <span className="hidden sm:inline">View Chart</span>
              <span className="sm:hidden">Chart</span>
            </Button>
          </div>
        </div>

        {isAlertsExpanded && totalAlerts > 0 && (
          <div className="mt-4 pt-4 border-t border-border space-y-2">
            {criticalAlerts?.map((alert, idx) => (
              <button
                key={`critical-${idx}`}
                onClick={() => handleAlertClick(alert)}
                className="treatment-alert critical w-full text-left"
              >
                <Icon name="AlertCircle" size={20} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{alert?.title}</p>
                  <p className="text-sm opacity-90 truncate">{alert?.message}</p>
                </div>
                <Icon name="ChevronRight" size={18} />
              </button>
            ))}

            {warningAlerts?.map((alert, idx) => (
              <button
                key={`warning-${idx}`}
                onClick={() => handleAlertClick(alert)}
                className="treatment-alert warning w-full text-left"
              >
                <Icon name="AlertTriangle" size={20} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{alert?.title}</p>
                  <p className="text-sm opacity-90 truncate">{alert?.message}</p>
                </div>
                <Icon name="ChevronRight" size={18} />
              </button>
            ))}

            {infoAlerts?.map((alert, idx) => (
              <button
                key={`info-${idx}`}
                onClick={() => handleAlertClick(alert)}
                className="treatment-alert info w-full text-left"
              >
                <Icon name="Info" size={20} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{alert?.title}</p>
                  <p className="text-sm opacity-90 truncate">{alert?.message}</p>
                </div>
                <Icon name="ChevronRight" size={18} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientContextBar;